import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { deliveryMethodHelpers, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import { getNextPage } from '@/DeliveryMethodsPage/helpers'
import { getOrderAmount } from '@/helpers'
import { getSingleRefundMethodToApply } from '@/helpers/refundMethodStep/refundMethodStep'
import services from '@/services/services'

import { getShippingAddress } from './helpers'

const useDeliveryMethods = () => {
  const { context, setContext } = useContext(AppContext)
  const { loading, fetchData } = useFetch(services.getDeliveryMethods, { doInitialRequest: false })
  const [specialConstraints, setSpecialConstraints] = useState(null)
  const [deliveryMethod, setDeliveryMethod] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { fetchData: addDeliveryMethodToReturn, loading: addingDeliveryMethod } = useFetch(
    services.addDeliveryMethodToReturn,
    {
      doInitialRequest: false,
    }
  )

  const { getDeliveryMethodsConstraints } = deliveryMethodHelpers()

  useEffect(() => {
    setSpecialConstraints(getDeliveryMethodsConstraints({ deliveryMethod }))
  }, [deliveryMethod])

  const updateAndSaveDeliveryMethodInContext = useCallback(
    deliveryMethod => {
      setDeliveryMethod(deliveryMethod)
      setContext(context => ({
        ...context,
        orderInfo: {
          ...context.orderInfo,
          deliveryMethod,
          selectedTimeRange: null,
          shipping_address: getShippingAddress(
            context.orderInfo.shipping_address,
            deliveryMethod,
            context.orderInfo.original_shipping_address
          ),
        },
      }))
    },
    [setContext]
  )

  const updateDeliveryMethods = useCallback(
    deliveryMethods => {
      setContext(context => ({ ...context, deliveryMethods }))
    },
    [setContext]
  )

  useEffect(() => {
    if (!context?.merchantInfo?.preferences?.disable_preselected_delivery_methods) {
      updateAndSaveDeliveryMethodInContext(context.deliveryMethods[0])
    }
  }, [
    context.deliveryMethods,
    context?.merchantInfo?.preferences?.disable_preselected_delivery_methods,
    updateAndSaveDeliveryMethodInContext,
  ])

  const addDeliveryMethodAndGoToNextPage = async ({
    returnId,
    payload,
    isExchangeOrMixed,
    needsExchangeAddress,
    onError,
  }) => {
    try {
      const data = await addDeliveryMethodToReturn({
        returnId: returnId,
        orderId: context.orderInfo.id,
        payload: payload,
      })

      if (data) {
        const orderAmount = getOrderAmount(data)
        const refundMethodToApply = getSingleRefundMethodToApply({
          context,
          allowedRefundMethods: data.allowed_refund_methods,
        })

        setContext(context => ({
          ...context,
          orderInfo: {
            ...data,
            ...context.orderInfo,
            refundType: refundMethodToApply,
            isRefundStepSkipped: Boolean(refundMethodToApply),
            currency: orderAmount?.currency,
            is_instant_exchange_allowed: data.is_instant_exchange_allowed,
            allowed_refund_methods: data.allowed_refund_methods,
          },
          orderAmount,
        }))

        const nextPage = getNextPage({
          isExchangeOrMixed,
          needsExchangeAddress,
          allowedRefundMethods: data.allowed_refund_methods,
          refundMethodToApply,
        })

        navigate(nextPage, { state: { from: location } })
      }
    } catch (error) {
      onError(error)
    }
  }

  return {
    getDeliveryMethods: fetchData,
    deliveryMethods: context?.deliveryMethods || [],
    deliveryMethod,
    setDeliveryMethod,
    updateAndSaveDeliveryMethodInContext,
    loading,
    updateDeliveryMethods,
    specialConstraints,
    addDeliveryMethodAndGoToNextPage,
    addingDeliveryMethod,
  }
}

export default useDeliveryMethods
